import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Quote, ExternalLink, Loader2 } from "lucide-react";

/**
 * Spreuk van de Dag — rotating podcast quote for the logged-in user.
 * The backend advances the index once per UTC calendar day per user.
 */
export default function QuoteOfTheDay() {
  const { t, lang } = useI18n();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/podcasts/quote-of-the-day", { params: { lang } });
        if (!cancelled) setQ(data);
      } catch {
        if (!cancelled) setErr(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  if (err) return null; // silently skip if endpoint fails — never block the diary
  if (loading || !q) {
    return (
      <section
        data-testid="diary-quote-of-day-loading"
        className="mb-14 border border-[#C1B49A] bg-[#EAE0CA]/40 p-6 md:p-8 flex items-center gap-3 text-[#4A4238]"
      >
        <Loader2 size={16} className="animate-spin text-[#8B3A3A]" />
        <span className="text-sm">{t("quote_of_day.loading")}</span>
      </section>
    );
  }

  return (
    <section
      data-testid="diary-quote-of-day"
      className="mb-14 border border-[#C1B49A] bg-[#EAE0CA]/40 p-6 md:p-10 relative"
    >
      <Quote
        size={64}
        className="absolute top-4 right-4 text-[#8B3A3A]/15"
        strokeWidth={1}
        aria-hidden="true"
      />
      <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2">
        <span>{t("quote_of_day.eyebrow")}</span>
        <span className="font-mono text-[10px] text-[#7A7061]">
          #{q.number} / 321
        </span>
      </p>
      <blockquote
        data-testid="diary-quote-text"
        className="font-display text-2xl md:text-3xl text-[#1A1918] leading-snug italic mb-5 max-w-3xl"
      >
        {q.quote}
      </blockquote>
      <p
        data-testid="diary-quote-explanation"
        className="text-[15px] text-[#4A4238] leading-relaxed max-w-3xl mb-6"
      >
        {q.explanation}
      </p>
      {q.spotify_url ? (
        <a
          href={q.spotify_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="diary-quote-spotify-link"
          className="inline-flex items-center gap-2 font-display text-lg text-[#8B3A3A] hover:text-[#1A1918] transition-colors border-b border-[#8B3A3A]/60 hover:border-[#1A1918]"
        >
          {q.title}
          <ExternalLink size={14} />
        </a>
      ) : (
        <span
          data-testid="diary-quote-title"
          className="font-display text-lg text-[#1A1918]"
        >
          {q.title}
        </span>
      )}
    </section>
  );
}
